/**
 * BoardFilesPanel - Left sidebar panel for board file attachments
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Files, Upload, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBoardPanelsStore } from '@stores/boardPanelsStore';
import { toast } from '@components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import CollapsiblePanel from './CollapsiblePanel';
import BoardFileItem from './BoardFileItem';
import AttachmentViewer from '../AttachmentViewer';
import { boardFilesApi, type Attachment } from '@services/api/boardFiles';

interface BoardFilesPanelProps {
  boardId: string;
}

export default function BoardFilesPanel({ boardId }: BoardFilesPanelProps) {
  const { t } = useTranslation();
  const {
    leftPanelOpen,
    toggleLeftPanel,
    setLeftPanelOpen,
    files,
    isLoadingFiles,
    isUploading,
    uploadProgress,
    filesError,
    fetchFiles,
    uploadFile,
    deleteFile,
    refreshFileUrl,
  } = useBoardPanelsStore();

  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFile, setDeletingFile] = useState<{ id: string; filename: string } | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Load files when panel opens
  useEffect(() => {
    if (leftPanelOpen && boardId) {
      void fetchFiles(boardId);
    }
  }, [leftPanelOpen, boardId, fetchFiles]);

  const handleFileSelect = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      for (const file of Array.from(fileList)) {
        try {
          await uploadFile(boardId, file);
          toast.success(t('common.uploadedFile', { filename: file.name }));
        } catch (error) {
          console.error('Failed to upload file:', error);
          const message = error instanceof Error ? error.message : 'Upload failed';
          toast.error(t('common.uploadFailed', { filename: file.name, message }));
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [boardId, uploadFile, t]
  );

  const handleDelete = (fileId: string, filename: string) => {
    setDeletingFile({ id: fileId, filename });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingFile) return;

    try {
      await deleteFile(deletingFile.id);
      toast.success(t('board.fileDeleted'));
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error(t('board.failedDeleteFile'));
    } finally {
      setShowDeleteConfirm(false);
      setDeletingFile(null);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const refreshed = await refreshFileUrl(attachment.id);
      if (refreshed.url) {
        window.open(refreshed.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error(t('board.failedDownloadFile'));
    }
  };

  const handleView = (attachment: Attachment) => {
    setViewingAttachment(attachment);
  };

  const getAttachmentUrl = useCallback(
    async (id: string): Promise<string> => {
      const refreshed = await boardFilesApi.getAttachment(id);
      return refreshed.url || '';
    },
    []
  );

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      void handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  return (
    <>
      <CollapsiblePanel
        side="left"
        isOpen={leftPanelOpen}
        onToggle={toggleLeftPanel}
        onClose={() => setLeftPanelOpen(false)}
        title={t('board.filesTitle')}
        icon={Files}
        actions={
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={clsx(
              'rounded p-1.5',
              'text-neutral-500 hover:text-neutral-700',
              'dark:text-neutral-400 dark:hover:text-neutral-200',
              'hover:bg-neutral-100 dark:hover:bg-neutral-800',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-colors'
            )}
            title={t('board.uploadFile')}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
          </button>
        }
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => void handleFileSelect(e.target.files)}
        />

        <div
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="flex h-full flex-col p-4"
        >
          {/* Drag overlay */}
          {isDragging && (
            <div
              className={clsx(
                'absolute inset-4 z-10',
                'flex flex-col items-center justify-center',
                'rounded-lg border-2 border-dashed',
                'border-primary-500 bg-primary-50 dark:bg-primary-900/20',
                'text-primary-600 dark:text-primary-400'
              )}
            >
              <Upload className="mb-2 h-8 w-8" />
              <span className="text-sm font-medium">{t('board.dropFilesHere')}</span>
            </div>
          )}

          {/* Upload progress */}
          {isUploading && uploadProgress > 0 && (
            <div className="mb-4 rounded-lg bg-neutral-100 p-3 dark:bg-neutral-800">
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">{t('board.uploading')}</span>
                <span className="text-neutral-500">{uploadProgress}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                <div
                  className="h-full rounded-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error state */}
          {filesError && (
            <div
              className={clsx(
                'mb-4 flex items-center gap-2 rounded-lg p-3',
                'bg-red-50 text-red-600',
                'dark:bg-red-900/20 dark:text-red-400'
              )}
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">{filesError}</span>
            </div>
          )}

          {/* Loading state */}
          {isLoadingFiles ? (
            <div className="flex flex-1 flex-col items-center justify-center text-neutral-500">
              <Loader2 className="mb-2 h-6 w-6 animate-spin" />
              <span className="text-sm">{t('board.loadingFiles')}</span>
            </div>
          ) : files.length === 0 ? (
            /* Empty state */
            <button
              onClick={() => fileInputRef.current?.click()}
              className={clsx(
                'flex flex-1 flex-col items-center justify-center rounded-lg',
                'border-2 border-dashed border-neutral-300 dark:border-neutral-700',
                'text-neutral-500 hover:border-neutral-400 hover:text-neutral-600',
                'dark:text-neutral-400 dark:hover:border-neutral-600 dark:hover:text-neutral-300',
                'transition-colors'
              )}
            >
              <Files className="mb-2 h-8 w-8" />
              <span className="text-sm font-medium">{t('board.noFilesYet')}</span>
              <span className="text-xs">{t('board.dropOrClickUpload')}</span>
            </button>
          ) : (
            /* Files list */
            <div className="space-y-2">
              {files.map((file) => (
                <BoardFileItem
                  key={file.id}
                  attachment={file}
                  onView={() => handleView(file)}
                  onDownload={() => void handleDownload(file)}
                  onDelete={() => handleDelete(file.id, file.filename)}
                />
              ))}
            </div>
          )}
        </div>
      </CollapsiblePanel>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingFile(null);
        }}
        onConfirm={() => void handleConfirmDelete()}
        title={t('board.deleteFileTitle')}
        description={t('board.deleteFileDescription', { filename: deletingFile?.filename ?? '' })}
        confirmLabel={t('common.delete')}
        variant="danger"
      />

      {/* Attachment viewer - same as TaskAttachments */}
      <AttachmentViewer
        attachment={viewingAttachment}
        attachments={files}
        isOpen={viewingAttachment !== null}
        onClose={() => setViewingAttachment(null)}
        onNavigate={setViewingAttachment}
        getAttachmentUrl={getAttachmentUrl}
      />
    </>
  );
}
